import axios from 'axios';
import { settings } from './Settings';
import { Header } from './components/Header';
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Box, Grid, Popover, CircularProgress } from '@mui/material';

const apiURL = settings.baseUrl;
const imageUriPath = settings.imageUriPath;
const db = settings.MongodbAppName;

const isAppleDevice = () => /iPad|iPhone|iPod|Macintosh/i.test(navigator.userAgent);

const getFormattedDate = (date, format) => {
  if (isAppleDevice()) {
    return moment.utc(date).format(format);
  }
  return moment(date).format(format);
};

const getDateObject = (date) => {
  if (isAppleDevice()) {
    return moment.utc(date);
  }
  return moment(date);
};

function HomeComponentMobile() {
  const [formattedCalendarDates, setFormattedCalendarDates] = useState([]);
  const [currentYear, setCurrentYear] = useState(() =>
    isAppleDevice() ? moment.utc().year() : new Date().getFullYear()
  );
  const [currentMonth, setCurrentMonth] = useState(() =>
    isAppleDevice() ? moment.utc().month() : new Date().getMonth()
  );
  const navigate = useNavigate();
  const [currentDay, setCurrentDay] = useState({
    day: getFormattedDate(new Date(), 'DD'),
    week: getFormattedDate(new Date(), 'ddd'),
    dated: getFormattedDate(new Date(), 'MMMM Do YYYY'),
  });
  const [todayIs, setTodayIs] = useState(moment().format('DD-MM-YYYY')); // Store current date
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const [hideSide, setHideSide] = useState(false);
  const [currentWeek, setCurrentWeek] = useState([]);
  const [viewMode, setViewMode] = useState('month');
  const [popShow, setPopShow] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  const organizeBatches = (arr) => {
    const organized = {};

    arr.forEach((obj) => {
      const date = moment.utc(obj.date).format('YYYY-MM-DD');
      const title = obj.course?.title || 'Untitled';

      if (!organized[date]) organized[date] = {};
      if (!organized[date][title]) organized[date][title] = [];
      organized[date][title].push(obj);
    });

    return Object.entries(organized).map(([date, courses]) => ({
      date,
      batches: Object.entries(courses).map(([course, batches]) => ({
        course,
        batches,
      })),
    }));
  };

  useEffect(() => {
    getCourses();
  }, []);

  const getCourses = async () => {
    setLoading(true);
    const collection = 'courses';
    const filter = { status: 'true' };
    const queryParams = new URLSearchParams({
      db,
      collection,
      filter: JSON.stringify(filter),
    });
    try {
      const response = await axios.get(`/read?${queryParams.toString()}`, {
        baseURL: apiURL,
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses: ', error);
    } finally {
      setLoading(false);
    }
  };

  const getColor = (c, d) => {
    for (var ind in courses) {
      if (courses[ind].title === c) {
        var hex = courses[ind].color.replace('#', '');
        const opacity = d ? 1 : 0.2;
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }
    }
  };

  const getCalendarDates = async (year, month) => {
    try {
      const yearNum = parseInt(year);
      const monthNum = parseInt(month);

      const dateStr = `${yearNum}-${String(monthNum + 1).padStart(2, '0')}-01`;
      const firstDay = isAppleDevice()
        ? moment.utc(dateStr)
        : moment(dateStr);

      const startDayIndex = firstDay.day();

      const daysInMonth = firstDay.daysInMonth();

      const calendarDates = [];

      for (let i = 0; i < startDayIndex; i++) {
        calendarDates.push(null);
      }

      for (let i = 1; i <= daysInMonth; i++) {
        const currentDate = isAppleDevice()
          ? moment.utc(`${yearNum}-${String(monthNum + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`)
          : moment(`${yearNum}-${String(monthNum + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`);

        calendarDates.push({
          date: currentDate,
          dayOfMonth: i
        });
      }

      const remaining = 7 - (calendarDates.length % 7);
      if (remaining < 7) {
        for (let i = 0; i < remaining; i++) {
          calendarDates.push(null);
        }
      }

      return calendarDates;
    } catch (error) {
      console.error('Error generating calendar dates:', error);
      return [];
    }
  };


  const isToday = (date) => {
    const appleDevice = isAppleDevice();
    return appleDevice ?
      moment.utc(date).isSame(moment.utc(), 'day') :
      moment(date).isSame(moment(), 'day');
  };

  const isDifferentMonth = (date) => {
    const appleDevice = isAppleDevice();
    return appleDevice ?
      moment.utc(date).month() !== currentMonth :
      date.getMonth() !== currentMonth;
  };

  const handleMonthChange = (direction) => {
    if (direction === 'next') {
      nextMonth();
    } else {
      prevMonth();
    }
  };

  const setCurrentDate = (d) => {
    setCurrentDay({
      day: getFormattedDate(d, 'DD'),
      week: getFormattedDate(d, 'ddd'),
      dated: getFormattedDate(d, 'MMMM Do YYYY'),
    });
  };


  const prevMonth = async () => {
    if (currentMonth === 0) {
      setCurrentYear(prevYear => prevYear - 1);
      setCurrentMonth(11);
    } else {
      setCurrentMonth(prevMonth => prevMonth - 1);
    }
  };

  const nextMonth = async () => {
    if (currentMonth === 11) {
      setCurrentYear(prevYear => prevYear + 1);
      setCurrentMonth(0);
    } else {
      setCurrentMonth(prevMonth => prevMonth + 1);
    }
  };

  const switchToMonthView = () => {
    setViewMode('month');
  };

  const switchToWeekView = () => {
    setViewMode('week');
  };

  const switchToListView = () => {
    setViewMode('day');
  };

  const [selectedBatch, setSelectedBatch] = useState({});

  const setCurrentBatch = (q, dated) => {
    const appleDevice = isAppleDevice();
    const currentDated = appleDevice ?
      moment.utc(dated).format('MMMM Do YYYY') :
      moment(dated).format('MMMM Do YYYY');
    setSelectedBatch({ ...q, currentDated });
  };

  const togglePopup = (event) => {
    if (!popShow) {
      openPopover(event);
    } else {
      closePopover();
    }
    setPopShow(!popShow);
  };
  const [search, setSearch] = useState('');

  const [thisWeek, setThisWeek] = useState(1);
  const [thisDay, setThisDay] = useState(1);
  const [thisDayData, setThisDayData] = useState(null);

  useEffect(() => {
    if (viewMode === 'week') {
      onWeekChange(thisWeek);
    }
    if (viewMode === 'day') {
      onDayChange(thisDay);
      setCurrentDate(
        `${new Date(currentYear + '-' + (currentMonth + 1) + '-' + thisDay)}`
      );
    }
  }, [thisDay, thisWeek, formattedCalendarDates, viewMode]);

  const onWeekChange = async (thisWeek) => {
    const totalDays = formattedCalendarDates.length;
    const totalWeeks = totalDays / 7;
    if (thisWeek <= totalWeeks && thisWeek > 0) {
      var arr = [];
      for (var i = 0; i < 7; i++) {
        arr.push(formattedCalendarDates[(thisWeek - 1) * 7 + i]);
      }
      setCurrentWeek(arr);

    } else if (thisWeek < 1) {
      await prevMonth();
    } else {
      await nextMonth();
      setThisWeek(1);
    }
  };

  const onDayChange = async (thisDay) => {
    const fullMonth = [];
    formattedCalendarDates.map((item) => {
      if (item) {
        fullMonth.push(item);
      }
    });

    const totalDays = fullMonth.length;


    if (thisDay <= totalDays && thisDay > 0) {
      const data = fullMonth[thisDay - 1];

      setThisDayData(data);
    } else if (thisDay < 1) {
      await prevMonth();
    } else {
      await nextMonth();
      setThisDay(1);
    }
  };

const getScheduledBatches = async (startDate, endDate, searchTerm = '') => {
  try {
    setLoading(true);

    const formattedStartDate = moment(startDate, "YYYY-MM-DD").format("YYYY-MM-DD");
    const formattedEndDate = moment(endDate, "YYYY-MM-DD").format("YYYY-MM-DD");

    const filter = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      search: searchTerm.trim().toLowerCase()
    };

    const response = await axios.get(`/batches/search?${new URLSearchParams({
      db,
      collection: 'batch',
      ...filter
    }).toString()}`, { baseURL: apiURL });

    // Multi-field search like PC
    const filteredData = response.data.filter(batch => {
      const lowerSearch = filter.search;

      return (
        batch.course?.title?.toLowerCase().includes(lowerSearch) ||
        batch.batch_no?.toString().toLowerCase().includes(lowerSearch) ||
        batch.language?.toLowerCase().includes(lowerSearch) ||
        batch.teacher?.name?.toLowerCase().includes(lowerSearch)
      );
    });

    const calendarDatesData = await getCalendarDates(currentYear, currentMonth);

    const formattedDates = calendarDatesData.map(item => {
      if (!item) return null;

      const itemDate = item.date;

      const preSch = filteredData.filter(batch => {
        const batchMoment = isAppleDevice()
          ? moment.utc(batch.date)
          : moment(batch.date);

        const itemMoment = isAppleDevice()
          ? moment.utc(itemDate)
          : moment(itemDate);

        return batchMoment.isSame(itemMoment, 'day');
      });

      const now = isAppleDevice() ? moment.utc() : moment();

      return {
        date: itemDate,
        dayOfMonth: item.dayOfMonth,
        isToday: now.isSame(itemDate, 'day'),
        isDifferentMonth: itemDate.month() !== currentMonth,
        schedule: organizeBatches(preSch)
      };
    });

    setFormattedCalendarDates(formattedDates);
  } catch (error) {
    console.error('Error fetching batches:', error);
  } finally {
    setLoading(false);
  }
};



  useEffect(() => {
    const loadCalendar = async () => {
      try {
        const startDate = isAppleDevice()
          ? moment.utc([currentYear, currentMonth, 1])
          : moment([currentYear, currentMonth, 1]);

        const endDate = startDate.clone().endOf('month');

        await getScheduledBatches(startDate, endDate);
      } catch (error) {
        console.error('Error loading calendar:', error);
      }
    };

    loadCalendar();
  }, [currentMonth, currentYear]);

  const renderDate = (dateObj) => {
    if (!dateObj || !dateObj.date) return '';
    return dateObj.dayOfMonth;
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const openPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  const isOpenPopover = Boolean(anchorEl);
  const popoverId = isOpenPopover ? 'simple-popover' : undefined;

  const LoadingSpinner = () => (
    <div className="flex justify-center items-center w-full h-[400px]">
      <CircularProgress />
    </div>
  );

  return (
    <div className="mb-20">
      <Header
        title="QC Calendar"
        description="You can check course schedules according to the calendar dates and timings."
      />
      <div className="grid grid-cols-12 gap-2 container">
        <div className={`${hideSide ? 'col-span-12 col-start-1' : 'col-span-10 col-start-2'}`}>
          <div className="flex flex-col sm:flex-row w-full mx-4 justify-between items-center">
            <div className="flex w-full sm:w-auto justify-start my-2 sm:my-0">
              <div className="sm:mx-4">
                <svg
                  onClick={() => setHideSide(!hideSide)}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-6 hidden sm:flex h-6 cursor-pointer mr-0 sm:mr-auto"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              </div>
              {viewMode === 'month' && (
                <div className="mx-4 w-full sm:w-auto">
                  <div className="text-xl font-semibold mr-10 flex w-full sm:w-auto sm:justify-start sm:items-start justify-between items-center">
                    <svg
                      onClick={prevMonth}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-6 h-6 mx-2 ml-0"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 19.5 8.25 12l7.5-7.5"
                      />
                    </svg>
                    {monthNames[currentMonth]}{' '}
                    <span className="mr-1">
                      {viewMode === 'day' && `, ${currentDay.day}`}
                    </span>{' '}
                    {currentYear}
                    <svg
                      onClick={nextMonth}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-6 h-6 mx-2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.25 4.5 7.5 7.5-7.5 7.5"
                      />
                    </svg>
                  </div>
                </div>
              )}
              {viewMode === 'week' && (
                <div className="mx-4 w-full sm:w-auto">
                  <div className="text-xl font-semibold mr-10 flex w-full sm:w-auto sm:justify-start sm:items-start justify-between items-center">
                    <svg
                      onClick={() => setThisWeek((thisWeek) => thisWeek - 1)}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-6 h-6 mx-2 ml-0"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 19.5 8.25 12l7.5-7.5"
                      />
                    </svg>
                    {monthNames[currentMonth]}{' '}
                    <span className="mr-1">
                      {viewMode === 'day' && `, ${currentDay.day}`}
                    </span>{' '}
                    {currentYear}
                    <svg
                      onClick={() => setThisWeek((thisWeek) => thisWeek + 1)}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-6 h-6 mx-2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.25 4.5 7.5 7.5-7.5 7.5"
                      />
                    </svg>
                  </div>
                </div>
              )}
              {viewMode === 'day' && (
                <div className="mx-4 w-full sm:w-auto">
                  <div className="text-xl font-semibold mr-10 flex w-full sm:w-auto sm:justify-start sm:items-start justify-between items-center">
                    <svg
                      onClick={() => setThisDay((thisDay) => thisDay - 1)}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-6 h-6 mx-2 ml-0"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 19.5 8.25 12l7.5-7.5"
                      />
                    </svg>
                    {monthNames[currentMonth]}
                    <span className="mx-1">{thisDay}</span>
                    {currentYear}
                    <svg
                      onClick={() => setThisDay((thisDay) => thisDay + 1)}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-6 h-6 mx-2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.25 4.5 7.5 7.5-7.5 7.5"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </div>
            <div className="w-full flex flex-col sm:flex-col">

              <div className="border flex flex-row w-full sm:w-auto rounded-full p-2 my-2 sm:my-0">
                <button
                  onClick={switchToMonthView}
                  className={`px-4 py-2 mr-2 rounded-full w-full sm:w-[100px] ${viewMode === 'month' ? 'bg-blue-500 text-white' : ''
                    }`}
                >
                  Month
                </button>
                <button
                  onClick={switchToWeekView}
                  className={`px-4 py-2 w-full sm:w-[100px] rounded-full ${viewMode === 'week' ? 'bg-blue-500 text-white' : ''
                    }`}
                >
                  Week
                </button>
                <button
                  onClick={switchToListView}
                  className={`px-4 py-2 w-full sm:w-[100px] rounded-full ${viewMode === 'day' ? 'bg-blue-500 text-white' : ''
                    }`}
                >
                  Day
                </button>
              </div>
<form
  onSubmit={(e) => {
    e.preventDefault();
    const startDate = moment(`${currentYear}-${currentMonth + 1}-1`).format('YYYY-MM-DD');
    const endDate = moment(startDate).endOf('month').format('YYYY-MM-DD');
    getScheduledBatches(startDate, endDate, search);
  }}
>

                <div className="flex justify-center sm:justify-end items-center border border-gray-300 rounded-full p-2 py-1 mt-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                    />
                  </svg>
                  <input
                    onChange={(e) => setSearch(e.target.value)}
                    type="text"
                    placeholder="Search Course"
                    className="border-0 w-[200px] text-sm no-border "
                  />
                  <button type="submit">Search</button>
                </div>
              </form>
            </div>
          </div>
          <div className="w-full flex gap-0">
            {loading ? (
              <LoadingSpinner />
            ) : (
              <>
                {viewMode === 'month' && formattedCalendarDates.length > 0 && (
                  <div className="w-full flex justify-center">
                    <div className="w-full">
                      <div className="grid grid-cols-7 gap-0">
                        {weekDays.map((day, index) => (
                          <div
                            key={index}
                            className="w-full text-center font-semibold border-gray-100 px-4 py-4 h-16 border-bottom bg-gray-50 flex justify-center items-center bg-white"
                          >
                            {day}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-0">
                        {formattedCalendarDates.map((item, index) => (
                          <div
                            key={index}
                            className=" w-full text-center border border-gray-100 p-2 min-h-40 flex justify-between items-start flex-col bg-white"
                          >
                            <p className="text-gray-600">
                              {renderDate(item)}
                            </p>
                            {item && (
                              <div className="schedule flex justify-start items-start flex-wrap">
                                {item.schedule &&
                                  item.schedule.map((qt, qtIndex) => (
                                    <div
                                      onClick={togglePopup}
                                      key={qtIndex}
                                      className="w-full flex flex-wrap"
                                    >
                                      {qt.batches.map((bt, btIndex) => (
                                        <div
                                          key={btIndex}
                                          className="mx-1 my-1 cursor-pointer ml-0 flex justify-start items-center"
                                          onClick={() => {
                                            setCurrentBatch(bt, item.date);
                                          }}
                                        >
                                          <span
                                            style={{
                                              backgroundColor: getColor(bt.course),
                                              fontWeight: 'bold',
                                              fontSize: '9px',
                                            }}
                                            className="bg-gray-100 flex items-center rounded-full border text-[9px] px-[4px] py-[1px] leading-none"
                                          >

                                            <span
                                              style={{
                                                backgroundColor: getColor(bt.course, 1),
                                                width: '6px',
                                                height: '6px',
                                                display: 'inline-block',
                                                borderRadius: '9999px',
                                                marginRight: '4px',
                                              }}
                                            ></span>
                                            {bt.course}
                                          </span>
                                          {bt.batches.length > 0 && (
                                            <span
                                              className="bdg"
                                              style={{
                                                borderWidth: '1px',
                                                borderColor: 'black',
                                              }}
                                            >
                                              {bt.batches.length}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {viewMode === 'week' && formattedCalendarDates.length > 0 && (
                  <div className="grid grid-cols-7 gap-0 w-full mx-1">
                    {weekDays.map((day) => (
                      <div
                        key={day}
                        className="w-full text-center font-semibold  px-4 py-4 h-16 border-gray-100 border-bottom bg-gray-50 flex justify-center items-center bg-white"
                      >
                        {day}
                      </div>
                    ))}

                    {currentWeek.map((item, index) => (
                      <div
                        key={index}
                        className="text-center border border-gray-100 p-2 min-h-40 flex justify-between items-start flex-col bg-white"
                      >
                        <p className="text-gray-600">
                          {item ? renderDate(item) : ''}
                        </p>
                        {item && (
                          <div className="schedule flex justify-start items-start flex-wrap">
                            {item.schedule &&
                              item.schedule.map((qt, qtIndex) => (
                                <div
                                  onClick={togglePopup}
                                  key={qtIndex}
                                  className="w-full flex flex-wrap"
                                >
                                  {qt.batches.map((bt, btIndex) => (
                                    <div
                                      key={btIndex}
                                      className="mx-1 my-1 cursor-pointer ml-0 flex justify-start items-center"
                                      onClick={() => {
                                        setCurrentBatch(bt, item.date);
                                      }}
                                    >
                                      <span

                                        style={{
                                          backgroundColor: getColor(bt.course),
                                          fontWeight: 'bold',
                                          fontSize: '9px',
                                        }}
                                        className="bg-gray-100 flex items-center rounded-full border text-[9px] px-[4px] py-[1px] leading-none"
                                      >
                                        <span
                                          style={{
                                            backgroundColor: getColor(bt.course, 1),
                                            width: '6px',
                                            height: '6px',
                                            display: 'inline-block',
                                            borderRadius: '9999px',
                                            marginRight: '4px',
                                          }}
                                        ></span>
                                        {bt.course}
                                      </span>
                                      {bt.batches.length > 0 && (
                                        <span
                                          className="bdg"
                                          style={{
                                            borderWidth: '0.25px',
                                            borderColor: 'black',
                                          }}
                                        >
                                          {bt.batches.length}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {viewMode === 'day' && formattedCalendarDates.length > 0 && (
                  <div className="grid grid-cols-1 gap-0 w-full">
                    {weekDays.map((day) => (
                      <div key={day} className="w-full">
                        {day === currentDay.week && (
                          <div className="text-center font-semibold  p-2 py-2 h-24 border-bottom bg-gray-50 flex justify-center items-center bg-white">
                            {currentDay.week} - {currentDay.dated}
                          </div>
                        )}
                      </div>
                    ))}

                    {thisDayData && (
                      <div className="w-full">
                        {thisDayData && thisDayData.date && (
                          <div className="text-center border b-gray-100 p-2 flex justify-start items-start flex-col bg-white">
                            <div className="schedule flex justify-start items-start flex-wrap w-full">
                              <p
                                className="p-4"
                                style={{
                                  display:
                                    (!loading && !thisDayData.schedule) ||
                                      thisDayData.schedule === ''
                                      ? 'block'
                                      : 'none',
                                }}
                              >
                                No Schedule found
                              </p>
                              {thisDayData.schedule &&
                                thisDayData.schedule.map((qt, qtIndex) => (
                                  <div key={qtIndex} className="p-4 w-full mb-4">
                                    {qt.batches.map((n, nIndex) => (
                                      <div
                                        key={nIndex}
                                        className="ml-l mb-12"
                                        style={{ borderColor: getColor(n.course) }}
                                      >
                                        <div className="flex border-b border-gray-200 pb-4 justify-between items-center font-bold ">
                                          <Link
                                            to={`/batch-list/${n.course}/${currentDay.dated}`}
                                            state={{ batches: n?.batches || [] }}
                                            className="flex justify-start"
                                          >
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              strokeWidth="1.5"
                                              stroke="currentColor"
                                              className="w-6 h-6 mx-2"
                                              style={{
                                                color: getColor(n.course, 1),
                                              }}
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M6 6.878V6a2.25 2.25 0 0 1 2.25-2.25h7.5A2.25 2.25 0 0 1 18 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 0 0 4.5 9v.878m13.5-3A2.25 2.25 0 0 1 19.5 9v.878m0 0a2.246 2.246 0 0 0-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0 1 21 12v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6c0-.98.626-1.813 1.5-2.122"
                                              />
                                            </svg>
                                            <h4
                                              style={{
                                                color: getColor(n.course, 1),
                                              }}
                                            >
                                              {n.course}
                                            </h4>
                                          </Link>
                                        </div>
                                        <div className="flex flex-col gap-3">
                                          <Grid container spacing={2}>
                                            {n.batches.map((m, mIndex) => (
                                              <Grid item md={6} xs={12}>
                                                <Link
                                                  to={`/batch-list/${n.course}/${currentDay.dated}/${m._id}`}
                                                  key={mIndex}
                                                  className="w-full"
                                                >
                                                  {mIndex < 5 && (
                                                    <div className="w-full mb-3">
                                                      <div class="w-full flex justify-center items-center mb-6 px-4">
                                                        <img
                                                          src={`${imageUriPath}${m.imagePath}`}
                                                          style={{
                                                            height: '400px',
                                                            width: '350px',
                                                          }}
                                                        />
                                                      </div>
                                                      <div className="flex-1 w-full flex flex-col border-gray-500 pb-3">
                                                        <div className="flex mt-2 justify-start items-start pr-2 flex-col">
                                                          <div className="flex justify-start mx-2 text-md font-bold">
                                                            {m?.teacher?.name}
                                                          </div>
                                                          <div className="flex gap-2">
                                                            {m.batch_no && (
                                                              <p className="bgd flex justify-center items-center bg-blue-800 text-white rounded-full p-2 text-xs py-1">
                                                                BATCH NO{' '}
                                                                {m.batch_no}
                                                              </p>
                                                            )}
                                                            {m.date &&
                                                              moment(
                                                                m.initialDate
                                                              ).isAfter() &&
                                                              m.registrationLink && (
                                                                <a
                                                                  onClick={(e) => {
                                                                    e.preventDefault();
                                                                    window.open(
                                                                      m.registrationLink,
                                                                      '_blank'
                                                                    );
                                                                  }}
                                                                  href={
                                                                    m.registrationLink
                                                                  }
                                                                  target="_blank"
                                                                  className="bgd flex justify-center items-center bg-green-500 text-white rounded-full p-2 text-xs py-1"
                                                                >
                                                                  Enroll now
                                                                </a>
                                                              )}
                                                          </div>
                                                        </div>
                                                        <div className="flex mt-2 justify-start items-center pr-2">
                                                          <div className="flex justify-start mx-2 text-xs">
                                                            {m.language}
                                                          </div>
                                                          <div className="dots"></div>
                                                          <p className="xxs flex justify-center items-center rounded-md p-2 text-xxs py-1">
                                                            {m.location}
                                                          </p>
                                                        </div>
                                                        <div className="flex mt-2 justify-start items-center pr-2">
                                                          <div className="flex justify-start items-center mx-2 text-xs ">
                                                            {m.time} {m.timeAt}{' '}
                                                            <div className="dots mx-2"></div>{' '}
                                                            {m.hours} Hour{' '}
                                                            {m.minutes} Minutes
                                                          </div>
                                                        </div>
                                                        <div className="mx-2 mt-2 text-xs">
                                                          {m.initialDate &&
                                                            moment(
                                                              m.initialDate
                                                            ).isAfter() ? (
                                                            <div className="flex justify-start items-center  mt-2">
                                                              Start On
                                                              <div className="dots mx-2"></div>{' '}
                                                              {moment(
                                                                m.initialDate
                                                              ).format(
                                                                'Do MMM YYYY'
                                                              )}
                                                            </div>
                                                          ) : (
                                                            m.initialDate && (
                                                              <div className="flex justify-start items-center   mt-2">
                                                                Started On
                                                                <div className="dots mx-2"></div>{' '}
                                                                {moment(
                                                                  m.initialDate
                                                                ).format(
                                                                  'Do MMM YYYY'
                                                                )}
                                                              </div>
                                                            )
                                                          )}
                                                        </div>
                                                        {m.date &&
                                                          moment(
                                                            m.initialDate
                                                          ).isAfter() &&
                                                          m.registrationLink && (
                                                            <div className="mx-2 animate-updown text-green-700 font-bold text-start mt-4">
                                                              Upcoming Course
                                                            </div>
                                                          )}
                                                      </div>
                                                    </div>
                                                  )}
                                                </Link>
                                              </Grid>
                                            ))}
                                          </Grid>
                                          {n.batches.length > 5 && (
                                            <button className="bg-[#2271b1] text-white rounded-md p-2 w-full">
                                              {n.batches.length - 5} More Courses
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
              <Popover
  id={popoverId}
  open={isOpenPopover}
  onClose={togglePopup}
  anchorReference="anchorPosition"
  anchorPosition={{ top: 0, left: 0 }}
  PaperProps={{
    sx: {
      width: '100%',
      maxWidth: '100%',
      minHeight: '400px',
      top: '0px !important',
      left: '0px !important',
      borderRadius: 0,
      position: 'fixed',
    },
  }}
  transformOrigin={{
    vertical: 'top',
    horizontal: 'left',
  }}
>
  <Box sx={{ width: '100%' }}>
    {popShow && (
      <div className="popup w-full h-auto block">
        <div className="bg-white rounded-none p-4">
                <div className="flex pb-4 justify-between items-center font-bold">
                  <Link
                    to={`/batch-list/${selectedBatch.course}/${selectedBatch.currentDated}`}
                    state={{ batches: selectedBatch?.batches || [] }}
                    className="flex justify-start text-blue-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-6 h-6 mx-2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 6.878V6a2.25 2.25 0 0 1 2.25-2.25h7.5A2.25 2.25 0 0 1 18 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 0 0 4.5 9v.878m13.5-3A2.25 2.25 0 0 1 19.5 9v.878m0 0a2.246 2.246 0 0 0-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0 1 21 12v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6c0-.98.626-1.813 1.5-2.122"
                      />
                    </svg>
                    <h4>{selectedBatch.course}</h4>
                  </Link>

                  <div>
                    <svg
                      onClick={togglePopup}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-6 h-6 cursor-pointer"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18 18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                </div>
                {selectedBatch?.batches[0]?.course?.imagePath && (
                  <img
                    src={`${imageUriPath}${selectedBatch?.batches[0]?.course?.imagePath}`}
                    style={{ height: '200px', width: '100%' }}
                  />
                )}
                <div className="w-full">
                  {selectedBatch.batches.map((b, index) => (
                    <div className="border-b border-b-6 border-black mb-2 hover:bg-gray-200">
                      <Link
                        to={`/batch-list/${selectedBatch.course}/${selectedBatch.currentDated}/${b._id}`}
                        key={index}
                        className="w-full pb-3 mb-3"
                      >
                        <>
                          {b.date &&
                            moment(b.initialDate).isAfter() &&
                            b.registrationLink && (
                              <div className="mx-2 animate-updown text-green-700 font-bold text-start mt-4">
                                Upcoming Course
                              </div>
                            )}
                          <div className="flex mt-2 justify-between items-center w-full pr-2">
                            <div className="flex justify-start mx-2 text-sm font-bold">
                              {b.teacher?.name}
                            </div>
                            <div className="flex gap-2">
                              {b.batch_no && (
                                <p className="bgd flex justify-center items-center bg-blue-800 text-white rounded-full p-2 text-xs py-1">
                                  BATCH NO {b.batch_no}
                                </p>
                              )}
                              {b.date &&
                                moment(b.initialDate).isAfter() &&
                                b.registrationLink && (
                                  <a
                                    onClick={(e) => {
                                      e.preventDefault();
                                      window.open(b.registrationLink, '_blank');
                                    }}
                                    href={b.registrationLink}
                                    target="_blank"
                                    className="bgd flex justify-center items-center bg-green-500 text-white rounded-full p-2 text-xs py-1"
                                  >
                                    Enroll now
                                  </a>
                                )}
                            </div>
                          </div>
                          <div className="flex mt-2 justify-between items-center w-full pr-2">
                            <div className="flex justify-start mx-2 text-xs">
                              {b.language}
                            </div>
                            <p className="xxs flex justify-center items-center rounded-md p-2 text-xxs py-1">
                              {b.location}
                            </p>
                          </div>
                          <div className="flex mt-2 justify-between items-center w-full pr-2">
                            <div className="flex justify-start mx-2 text-xs font-bold">
                              {b.time} {b.timeAt} . {b.hours} Hour {b.minutes}{' '}
                              Minutes
                            </div>
                            <div className="flex justify-start mx-2 text-xs font-bold">
                              {b.initialDate &&
                                moment(b.initialDate).isAfter() ? (
                                <div className="flex justify-start items-center    mt-2">
                                  Start On:
                                  {moment(b.initialDate).format('Do MMM YYYY')}
                                </div>
                              ) : (
                                b.initialDate && (
                                  <div className="flex justify-start items-center    mt-2">
                                    Started On:
                                    {moment(b.initialDate).format(
                                      'Do MMM YYYY'
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </>
                      </Link>
                    </div>
                  ))}
                </div>
                {selectedBatch.batches.length > 5 && (
                  <button className="bg-[#2271b1] text-white rounded-md p-2 w-full">
                    {selectedBatch.batches.length - 5} More Courses
                  </button>
                )}
              </div>
            </div>
          )}
        </Box>
      </Popover>
    </div>
  );
}

export default HomeComponentMobile;
